import { NgIf } from '@angular/common';
import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { VisibilityService, VISIBILITY_OPTIONS } from '@df/ui/common/visibility.service';

export const onInViewChangeEvent = 'onInViewChange';

@Directive({
    selector: '[onInView]',
    standalone: true
})
export class OnInViewDirective extends NgIf implements OnInit, OnDestroy {
    @Input() threshold = VISIBILITY_OPTIONS.threshold;
    @Input() rootMargin = '300px';

    /**
     * In viewport subscription
     */
    private subscriptionViewport?: Subscription;

    private wrapper!: HTMLElement;

    constructor(
        _viewContainer: ViewContainerRef,
        templateRef: TemplateRef<any>,
        private visibilityService: VisibilityService,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private events: Events,
        private app: App,
        private timeoutService: TimeoutService
    ) {
        super(_viewContainer, templateRef);
    }

    ngOnInit(): void {
        if (this.app.isPlatformBrowser) {
            try {
                this.wrapper = this.wrap();
                this.subscribeViewportCheck();
                this.timeoutService.setTimeout(() => this.finish(true), 2000);
            } catch (err) {
                this.finish(true);
            }
        } else {
            this.ngIf = true;
        }
    }

    ngOnDestroy(): void {
        this.finish();
    }

    /**
     * Subscribes to viewport content changes
     */
    private subscribeViewportCheck() {
        if (!this.subscriptionViewport) {
            this.subscriptionViewport = this.visibilityService
                .isInView(this.wrapper, {
                    interaction: {
                        threshold: this.threshold,
                        rootMargin: this.rootMargin
                    },
                    initial: { rootMargin: '20px' }
                })
                .pipe(
                    filter<boolean>(visible => visible),
                    take(1)
                )
                .subscribe(() => this.finish(true));
        }
    }

    private finish(resolution = false) {
        if (this.subscriptionViewport) {
            if (resolution) {
                this.ngIf = true;
                this.events.broadcast(onInViewChangeEvent);
            }

            this.renderer.setProperty(this.wrapper, 'style', '');
            this.subscriptionViewport.unsubscribe();
            this.subscriptionViewport = undefined;
        }
    }

    private wrap() {
        const wrapper = this.renderer.createElement('div');
        this.renderer.setProperty(wrapper, 'style', 'min-height:300px');
        this.elementRef.nativeElement.parentNode.insertBefore(wrapper, this.elementRef.nativeElement);
        wrapper.appendChild(this.elementRef.nativeElement);

        return wrapper;
    }
}
